import React, { useEffect, useState, useContext } from "react";
import { useParams } from "react-router-dom";
import { doc, getDoc, updateDoc } from "firebase/firestore";
import { db } from "../firebase.config";
import UserContext from "../Context/Usercontext";
import EditUserProfile from "../Components/EditUserProfile";
import AddEventModal from "../Components/AddEventModal";
import AddCertificateModal from "../Components/AddCertificateModal";
import Navbar from "../Components/Navbar";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faEdit,
  faCalendarPlus,
  faMedal,
} from "@fortawesome/free-solid-svg-icons";

const UserProfile = () => {
  const { id: urlId } = useParams();
  const { userId: contextUserId } = useContext(UserContext);
  const [userInfo, setUserInfo] = useState({
    name: "",
    email: "",
    department: "",
    club: "",
    role: "",
    profileImageUrl: null,
    eventCount: 0,
    certificateCount: 0,
  });
  const [isEditing, setIsEditing] = useState(false);
  const [isEventModalOpen, setIsEventModalOpen] = useState(false);
  const [isCertificateModalOpen, setIsCertificateModalOpen] = useState(false);

  const userId = contextUserId || urlId;

  useEffect(() => {
    const fetchUserInfo = async () => {
      try {
        const docRef = doc(db, "users", userId);
        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) {
          const fetchedData = docSnap.data();
          const updatedUserInfo = {
            name: fetchedData.name || "",
            email: fetchedData.email || "",
            department: fetchedData.department || "",
            club: fetchedData.club || "",
            role: fetchedData.role || "",
            profileImageUrl: fetchedData.profileImageUrl || null,
            eventCount: fetchedData.eventCount || 0,
            certificateCount: fetchedData.certificateCount || 0,
          };
          setUserInfo(updatedUserInfo);
        } else {
          console.log("No such document!");
        }
      } catch (error) {
        console.error("Error fetching user information:", error);
      }
    };

    fetchUserInfo();
  }, [userId]);

  const handleEditToggle = () => {
    setIsEditing(!isEditing);
  };

  const handleSave = async () => {
    try {
      const userDocRef = doc(db, "users", userId);
      await updateDoc(userDocRef, userInfo);
      setIsEditing(false);
    } catch (error) {
      console.error("Error updating document:", error);
    }
  };

  const handleImageChange = (event) => {
    const imageUrl = event.target.value; // Assuming you have an input field for image URL
    setUserInfo((prevUserInfo) => ({
      ...prevUserInfo,
      profileImageUrl: imageUrl,
    }));
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 to-black text-gray-100">
      <Navbar />
      <div className="max-w-4xl mx-auto p-8">
        <div className="bg-gray-800 shadow-xl rounded-lg overflow-hidden">
          <div className="relative h-48 bg-gradient-to-r from-green-400 to-blue-500 flex items-center justify-center text-center">
            {userInfo.profileImageUrl && (
              <img
                src={userInfo.profileImageUrl}
                alt="Profile"
                className="absolute bottom-0 left-1/2 transform -translate-x-1/2 translate-y-1/2 w-32 h-32 rounded-full border-4 border-gray-900 object-cover"
              />
            )}
            <h2 className="text-3xl font-bold text-white absolute bottom-0 mb-20">
              {userInfo.name}
            </h2>
          </div>
          <div className="p-8">
            {isEditing ? (
              <EditUserProfile
                userInfo={userInfo}
                setUserInfo={setUserInfo}
                handleSave={handleSave}
                handleEditToggle={handleEditToggle}
              />
            ) : (
              <div className="space-y-4">
                <div className="text-lg">
                  <span className="font-semibold text-gray-400">Email:</span>{" "}
                  <span className="text-gray-300">{userInfo.email}</span>
                </div>
                <div className="text-lg">
                  <span className="font-semibold text-gray-400">
                    Department:
                  </span>{" "}
                  <span className="text-gray-300">{userInfo.department}</span>
                </div>
                <div className="text-lg">
                  <span className="font-semibold text-gray-400">Club:</span>{" "}
                  <span className="text-gray-300">{userInfo.club}</span>
                </div>
                <div className="text-lg">
                  <span className="font-semibold text-gray-400">Role:</span>{" "}
                  <span className="text-gray-300">{userInfo.role}</span>
                </div>
                <div className="flex justify-between items-center mt-6">
                  <div className="text-center">
                    <p className="text-3xl font-bold text-gray-100">
                      {userInfo.eventCount}
                    </p>
                    <p className="text-sm uppercase text-gray-400">Events</p>
                  </div>
                  <div className="text-center">
                    <p className="text-3xl font-bold text-gray-100">
                      {userInfo.certificateCount}
                    </p>
                    <p className="text-sm uppercase text-gray-400">
                      Certificates
                    </p>
                  </div>
                </div>
                <div className="flex flex-col sm:flex-row justify-center sm:space-x-4 sm:space-y-0 mt-8">
                  <button
                    onClick={handleEditToggle}
                    className="flex items-center px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 transition duration-300 mb-2 sm:mb-0"
                  >
                    <FontAwesomeIcon icon={faEdit} className="mr-2" />
                    Edit Profile
                  </button>
                  <button
                    onClick={() => setIsEventModalOpen(true)}
                    className="flex items-center px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition duration-300 mb-2 sm:mb-0"
                  >
                    <FontAwesomeIcon icon={faCalendarPlus} className="mr-2" />
                    Add Event
                  </button>
                  <button
                    onClick={() => setIsCertificateModalOpen(true)}
                    className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition duration-300"
                  >
                    <FontAwesomeIcon icon={faMedal} className="mr-2" />
                    Add Certificate
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      <AddEventModal
        isOpen={isEventModalOpen}
        onRequestClose={() => setIsEventModalOpen(false)}
        userId={userId}
      />
      <AddCertificateModal
        isOpen={isCertificateModalOpen}
        onRequestClose={() => setIsCertificateModalOpen(false)}
        userId={userId}
      />
    </div>
  );
};

export default UserProfile;
